import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Select,
  Textfield
} from '@clientbase/clientbase-library'
import { Box, MenuItem, Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { BillingsParams } from 'models/Billings'
import moment from 'moment'
import { rgba } from 'polished'

import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import {
  getStatusOptionCharge,
  getStatusStyleCharge
} from '../../ModalRecurrenceDetail.mappers'

// SC-11871: o limite é da empresa (capabilities.carne.max_billings). O padrão
// cobre token emitido antes da chave existir.
const DEFAULT_CARNE_MAX_BILLINGS = 60

type CarneDialogProps = {
  isOpen: boolean
  onClose: () => void
  recurrenceUuid: string
  totalCycles: number
  currentCycle: number
  billings: BillingsParams[]
}

const CarneDialog = ({
  isOpen,
  onClose,
  recurrenceUuid,
  totalCycles,
  currentCycle,
  billings
}: CarneDialogProps) => {
  const [startCycle, setStartCycle] = useState<string>('')
  const [billingCount, setBillingCount] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const { capabilities } = useAuthContext()

  const carneLimit =
    capabilities?.carne?.max_billings || DEFAULT_CARNE_MAX_BILLINGS
  const isInfinite = totalCycles === 0
  const startCycleNum = parseInt(startCycle, 10) || 1
  const maxBillings = isInfinite
    ? carneLimit
    : Math.max(0, Math.min(carneLimit, totalCycles - startCycleNum + 1))

  const sortedBillings = [...billings].sort((a, b) => {
    const cycleA = parseInt(a.recurrenceCycle || '0', 10)
    const cycleB = parseInt(b.recurrenceCycle || '0', 10)
    return cycleA - cycleB
  })

  useEffect(() => {
    if (isOpen && currentCycle > 0) {
      setStartCycle(String(currentCycle))
    }
  }, [isOpen, currentCycle])

  const validateBillingCount = (value: string, cycleNum?: number) => {
    const num = parseInt(value, 10)
    const cycle = cycleNum || startCycleNum
    const max = isInfinite
      ? carneLimit
      : Math.min(carneLimit, totalCycles - cycle + 1)

    if (value && num > max) {
      setError(
        isInfinite
          ? `Máximo de ${carneLimit} cobranças por carnê`
          : `Máximo de ${max} cobranças a partir do ciclo ${cycle}`
      )
    } else if (value && num < 1) {
      setError('Mínimo de 1 cobrança')
    } else {
      setError('')
    }
  }

  const handleCycleChange = (value: string) => {
    setStartCycle(value)
    validateBillingCount(billingCount, parseInt(value, 10))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '')
    setBillingCount(value)
    validateBillingCount(value)
  }

  const handleSubmit = async () => {
    const num = parseInt(billingCount, 10)
    if (!num || num < 1 || num > maxBillings) return

    setLoading(true)
    try {
      const response = await DataService({
        type: 'POST',
        url: '/v1/reports/carne',
        data: {
          filter_by: `recurrence_uuid=${recurrenceUuid},billing_count=${num},start_cycle=${startCycleNum}`
        }
      })

      if (response?.data?.error === false) {
        toast.success(
          'O carnê está sendo gerado. Você receberá uma notificação quando finalizar.',
          { ...toastProps }
        )
        handleClose()
      } else {
        toast.error(
          response?.data?.message ||
            response?.message ||
            'Erro ao solicitar o carnê',
          { ...toastProps }
        )
      }
    } catch {
      toast.error('Erro ao solicitar o carnê', { ...toastProps })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setStartCycle('')
    setBillingCount('')
    setError('')
    setLoading(false)
    onClose()
  }

  const isValid =
    billingCount !== '' &&
    parseInt(billingCount, 10) >= 1 &&
    parseInt(billingCount, 10) <= maxBillings &&
    startCycle !== ''

  return (
    <Dialog
      icon="receipt"
      title="Gerar Carnê"
      isOpenDialog={isOpen}
      setIsOpenDialog={() => handleClose()}
      PaperProps={{
        sx: {
          maxWidth: '450px',
          '& .MuiDialogContent-root': { textAlign: 'left' }
        }
      }}
    >
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Box>
          <Select
            label="A partir do ciclo"
            value={startCycle}
            onChange={(e) => handleCycleChange(e.target.value as string)}
            displayEmpty
            fullWidth
            size="small"
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography color="text.disabled" fontSize="14px">
                    Selecione um ciclo
                  </Typography>
                )
              }
              const billing = sortedBillings.find(
                (b) => b.recurrenceCycle === selected
              )
              if (!billing) return `Ciclo ${selected}`
              const statusInfo = getStatusOptionCharge(
                billing.status || '',
                billing.dueDate
              )
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography fontSize="14px">
                    Ciclo {billing.recurrenceCycle} -{' '}
                    {moment(billing.dueDate).format('DD/MM/YYYY')}
                  </Typography>
                  <Box
                    sx={{
                      padding: '2px 8px',
                      fontWeight: 600,
                      borderRadius: 9999,
                      fontSize: '13px',
                      ...getStatusStyleCharge(
                        billing.status || '',
                        billing.dueDate
                      )
                    }}
                  >
                    {statusInfo.label}
                  </Box>
                </Stack>
              )
            }}
          >
            {sortedBillings.map((billing) => {
              const cycle = billing.recurrenceCycle || '0'
              const isCurrent = parseInt(cycle, 10) === currentCycle
              const statusInfo = getStatusOptionCharge(
                billing.status || '',
                billing.dueDate
              )

              return (
                <MenuItem
                  key={billing.uuid}
                  value={cycle}
                  sx={{ py: 1, px: 1.5 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    spacing={1}
                  >
                    <Typography fontSize="14px">
                      Ciclo {cycle} -{' '}
                      {moment(billing.dueDate).format('DD/MM/YYYY')}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {isCurrent && (
                        <Box
                          sx={{
                            padding: '2px 8px',
                            fontWeight: 600,
                            borderRadius: 9999,
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            color: theme.palette.info[300],
                            background: rgba(theme.palette.info[300], 0.15)
                          }}
                        >
                          Atual
                        </Box>
                      )}
                      <Box
                        sx={{
                          padding: '2px 8px',
                          fontWeight: 600,
                          borderRadius: 9999,
                          fontSize: '13px',
                          whiteSpace: 'nowrap',
                          ...getStatusStyleCharge(
                            billing.status || '',
                            billing.dueDate
                          )
                        }}
                      >
                        {statusInfo.label}
                      </Box>
                    </Stack>
                  </Stack>
                </MenuItem>
              )
            })}
          </Select>
        </Box>

        <Textfield
          label="Quantidade de cobranças"
          placeholder="Ex: 12"
          value={billingCount}
          onChange={handleChange}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          error={!!error}
          helperText={error || ' '}
        />

        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Typography
            component="li"
            fontSize="14px"
            variant="smLight"
            color="text.secondary"
          >
            O carnê será gerado a partir da cobrança selecionada
          </Typography>
          <Typography
            component="li"
            fontSize="14px"
            variant="smLight"
            color="text.secondary"
          >
            Máximo de {carneLimit} cobranças por carnê
          </Typography>
          <Typography
            component="li"
            fontSize="14px"
            variant="smLight"
            color="text.secondary"
          >
            Cobranças Pendentes serão emitidas automaticamente
          </Typography>
          <Typography
            component="li"
            fontSize="14px"
            variant="smLight"
            color="text.secondary"
          >
            Cobranças Pagas serão incluídas no carnê
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variantButton="neutral"
            onClick={handleClose}
            disabled={loading}
            fullWidth
          >
            Cancelar
          </Button>
          <Button
            variantButton="primary"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            fullWidth
          >
            {loading ? 'Gerando...' : 'Gerar Carnê'}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  )
}

export default CarneDialog
